import React from 'react';
import { motion } from 'framer-motion';
import { Bike, Wrench, AlertCircle, CheckCircle, Battery, MapPin, Plus, Ban, Link } from 'lucide-react';
import { StatsCard } from '../components/StatsCard';
import { DataTable } from '../components/DataTable';
import { StatusBadge } from '../components/StatusBadge';
const bikes = [{
  id: 'BK-1001',
  model: 'Yamaha NMAX',
  plate: 'ABC-1234',
  rider: 'John Doe',
  status: 'active',
  mileage: '12,450 km',
  lastService: '2 weeks ago',
  battery: 'N/A'
}, {
  id: 'BK-1002',
  model: 'Honda PCX',
  plate: 'XYZ-5678',
  rider: 'Mike Ross',
  status: 'maintenance',
  mileage: '24,100 km',
  lastService: '1 month ago',
  battery: 'N/A'
}, {
  id: 'BK-1003',
  model: 'Electric Scooter X1',
  plate: 'EV-9988',
  rider: 'Sarah Connor',
  status: 'active',
  mileage: '5,200 km',
  lastService: '1 week ago',
  battery: '85%'
}, {
  id: 'BK-1004',
  model: 'Yamaha Aerox',
  plate: 'DEF-4321',
  rider: 'Unassigned',
  status: 'inactive',
  mileage: '8,900 km',
  lastService: '3 months ago',
  battery: 'N/A'
}, {
  id: 'BK-1005',
  model: 'Honda Beat',
  plate: 'GHI-8765',
  rider: 'Clark Kent',
  status: 'active',
  mileage: '18,300 km',
  lastService: '1 month ago',
  battery: 'N/A'
}, {
  id: 'BK-1006',
  model: 'Electric Scooter X1',
  plate: 'EV-7766',
  rider: 'Diana Prince',
  status: 'active',
  mileage: '3,400 km',
  lastService: '2 days ago',
  battery: '42%'
}];
const maintenanceQueue = [{
  id: 1,
  bike: 'BK-1002',
  issue: 'Brake pads replacement needed',
  flaggedBy: 'Mike Ross',
  date: '2 days ago',
  priority: 'high'
}, {
  id: 2,
  bike: 'BK-1004',
  issue: 'Oil change overdue',
  flaggedBy: 'System',
  date: '1 week ago',
  priority: 'medium'
}, {
  id: 3,
  bike: 'BK-1006',
  issue: 'Battery health check',
  flaggedBy: 'Diana Prince',
  date: 'Yesterday',
  priority: 'low'
}];
export function FleetManagement() {
  const columns = [{
    header: 'Bike Info',
    accessor: (row: any) => <div className="flex items-center gap-3" data-id="element-531">
          <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center text-slate-500" data-id="element-532">
            <Bike size={20} data-id="element-533" />
          </div>
          <div data-id="element-534">
            <p className="font-medium text-slate-900" data-id="element-535">{row.model}</p>
            <p className="text-xs text-slate-500 font-mono" data-id="element-536">{row.plate}</p>
          </div>
        </div>
  }, {
    header: 'Assigned Rider',
    accessor: (row: any) => <span className={row.rider === 'Unassigned' ? 'text-slate-400 italic' : 'text-slate-700'} data-id="element-537">
          {row.rider}
        </span>
  }, {
    header: 'Status',
    accessor: (row: any) => <StatusBadge status={row.status} data-id="element-538" />
  }, {
    header: 'Mileage',
    accessor: 'mileage'
  }, {
    header: 'Health/Battery',
    accessor: (row: any) => <div className="flex items-center gap-2" data-id="element-539">
          {row.battery !== 'N/A' ? <>
              <Battery size={16} className={parseInt(row.battery) > 50 ? 'text-green-500' : 'text-orange-500'} data-id="element-540" />
              <span className="text-sm text-slate-700" data-id="element-541">{row.battery}</span>
            </> : <span className="text-xs text-slate-400" data-id="element-542">Gasoline</span>}
        </div>
  }, {
    header: 'Last Service',
    accessor: 'lastService'
  }];
  return <div className="space-y-6" data-id="element-543">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6" data-id="element-544">
        <StatsCard title="Total Fleet" value="142" trend={4} icon={Bike} color="blue" delay={0} data-id="element-545" />
        <StatsCard title="Active Bikes" value="118" trend={2} icon={CheckCircle} color="green" delay={0.1} data-id="element-546" />
        <StatsCard title="Maintenance Needed" value="8" trend={-1} icon={Wrench} color="gold" delay={0.2} data-id="element-547" />
        <StatsCard title="Out of Service" value="16" trend={5} icon={AlertCircle} color="purple" delay={0.3} data-id="element-548" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-549">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.4
      }} className="lg:col-span-2" data-id="element-550">
          <DataTable title="Fleet Inventory" data={bikes} columns={columns} searchPlaceholder="Search by model, plate, or rider..." actions={row => <>
                <button className="p-1.5 text-slate-500 hover:text-famo-navy hover:bg-slate-100 rounded transition-colors" title="Track Location" data-id="element-552">
                  <MapPin size={18} data-id="element-553" />
                </button>
                <button className="p-1.5 text-blue-500 hover:bg-blue-50 rounded transition-colors" title="Assign Rider" data-id="element-554">
                  <Link size={18} data-id="element-555" />
                </button>
                <button className="p-1.5 text-orange-500 hover:bg-orange-50 rounded transition-colors" title="Schedule Maintenance" data-id="element-556">
                  <Wrench size={18} data-id="element-557" />
                </button>
                <button className="p-1.5 text-red-500 hover:bg-red-50 rounded transition-colors" title="Block/Decommission" data-id="element-558">
                  <Ban size={18} data-id="element-559" />
                </button>
              </>} data-id="element-551" />
        </motion.div>

        <div className="space-y-6" data-id="element-560">
          {/* Register New Bike Form */}
          <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-100" data-id="element-561">
            <h3 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2" data-id="element-562">
              <Plus size={20} className="text-famo-gold" data-id="element-563" />
              Register New Bike
            </h3>
            <div className="space-y-4" data-id="element-564">
              <div data-id="element-565">
                <label className="block text-xs font-medium text-slate-500 mb-1" data-id="element-566">
                  Bike ID
                </label>
                <input type="text" className="w-full bg-slate-50 border border-slate-200 rounded-lg text-sm px-4 py-2 focus:ring-2 focus:ring-famo-gold/50 focus:border-famo-gold outline-none" placeholder="e.g. BK-1007" data-id="element-567" />
              </div>
              <div data-id="element-568">
                <label className="block text-xs font-medium text-slate-500 mb-1" data-id="element-569">
                  Model
                </label>
                <input type="text" className="w-full bg-slate-50 border border-slate-200 rounded-lg text-sm px-4 py-2 focus:ring-2 focus:ring-famo-gold/50 focus:border-famo-gold outline-none" placeholder="e.g. Yamaha NMAX" data-id="element-570" />
              </div>
              <div className="grid grid-cols-2 gap-4" data-id="element-571">
                <div data-id="element-572">
                  <label className="block text-xs font-medium text-slate-500 mb-1" data-id="element-573">
                    Year
                  </label>
                  <input type="text" className="w-full bg-slate-50 border border-slate-200 rounded-lg text-sm px-4 py-2 focus:ring-2 focus:ring-famo-gold/50 focus:border-famo-gold outline-none" placeholder="2023" data-id="element-574" />
                </div>
                <div data-id="element-575">
                  <label className="block text-xs font-medium text-slate-500 mb-1" data-id="element-576">
                    Plate
                  </label>
                  <input type="text" className="w-full bg-slate-50 border border-slate-200 rounded-lg text-sm px-4 py-2 focus:ring-2 focus:ring-famo-gold/50 focus:border-famo-gold outline-none" placeholder="ABC-1234" data-id="element-577" />
                </div>
              </div>
              <div data-id="element-578">
                <label className="block text-xs font-medium text-slate-500 mb-1" data-id="element-579">
                  Initial Mileage (km)
                </label>
                <input type="number" className="w-full bg-slate-50 border border-slate-200 rounded-lg text-sm px-4 py-2 focus:ring-2 focus:ring-famo-gold/50 focus:border-famo-gold outline-none" placeholder="0" data-id="element-580" />
              </div>
              <button className="w-full bg-famo-gold text-white hover:opacity-90 rounded-lg px-4 py-2 text-sm font-medium transition-colors" data-id="element-581">
                Register Bike
              </button>
            </div>
          </div>

          {/* Maintenance Queue */}
          <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-100" data-id="element-582">
            <h3 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2" data-id="element-583">
              <Wrench size={20} className="text-orange-500" data-id="element-584" />
              Maintenance Queue
            </h3>
            <div className="space-y-4" data-id="element-585">
              {maintenanceQueue.map(item => <div key={item.id} className="border border-slate-100 rounded-lg p-3 bg-slate-50/50" data-id="element-586">
                  <div className="flex justify-between items-start mb-1" data-id="element-587">
                    <span className="font-mono font-bold text-slate-700" data-id="element-588">
                      {item.bike}
                    </span>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-bold uppercase ${item.priority === 'high' ? 'bg-red-100 text-red-700' : item.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' : 'bg-blue-100 text-blue-700'}`} data-id="element-589">
                      {item.priority}
                    </span>
                  </div>
                  <p className="text-sm text-slate-600 mb-2" data-id="element-590">{item.issue}</p>
                  <div className="flex justify-between text-xs text-slate-400 mb-3" data-id="element-591">
                    <span data-id="element-592">By: {item.flaggedBy}</span>
                    <span data-id="element-593">{item.date}</span>
                  </div>
                  <button className="w-full bg-white border border-slate-200 text-green-600 text-xs font-medium py-1.5 rounded hover:bg-green-50" data-id="element-594">
                    Mark Repaired
                  </button>
                </div>)}
            </div>
          </div>
        </div>
      </div>
    </div>;
}